/**
 * Navigation Utilities
 *
 * Centralized role-to-route mapping and navigation helpers.
 * Single source of truth for dashboard URLs by role.
 */

/**
 * Dashboard route mappings by role
 */
export const DASHBOARD_ROUTES = {
	admin: "/admin",
	designer: "/designer",
	client: "/portal",
	default: "/",
} as const;

export type Role = "admin" | "designer" | "client";

/**
 * Get dashboard URL for a user role.
 *
 * @param role - User's role (admin, designer, client)
 * @returns Dashboard URL path
 */
export function getDashboardUrl(role: string | undefined): string {
	if (!role) return DASHBOARD_ROUTES.default;
	return (
		DASHBOARD_ROUTES[role as keyof typeof DASHBOARD_ROUTES] ??
		DASHBOARD_ROUTES.default
	);
}

/**
 * Check if a role has access to a specific route.
 *
 * @param role - User's role
 * @param route - Route path to check
 * @returns Whether the role can access the route
 */
export function canAccessRoute(role: Role | undefined, route: string): boolean {
	if (!role) return false;

	// Admin can access everything
	if (role === "admin") return true;

	// Designer can access designer routes and shared routes
	if (role === "designer") {
		return route.startsWith("/designer") || route === "/";
	}

	// Client can only access portal routes
	if (role === "client") {
		return route.startsWith("/portal") || route === "/";
	}

	return false;
}

/**
 * Get redirect URL when access is denied.
 *
 * @param role - User's role (if authenticated)
 * @returns URL to redirect to
 */
export function getAccessDeniedRedirect(role: Role | undefined): string {
	// If authenticated, go to their dashboard
	if (role) return getDashboardUrl(role);
	// Otherwise, go to sign in
	return "/sign-in";
}
